import { DeviceMode, isComplexElement, isNodeV2, isSlotElement } from '@vedalib/editor'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { setNavbar, setProjectNavigation, updateBlock } from 'services/Store/Project/actions'
import { NavigationEnum } from 'services/Store/Project/enums'
import { childrenLens, elementValueItemsLens } from 'services/Store/Project/lenses'
import { moveNode, omitNode, rejectNode, setGridArea } from 'services/Store/Project/mutators/node'
import {
  getBlock,
  getDeviceMode,
  getSelectedNodeIds,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import store from 'services/Store/store'
import { t } from 'services/Translation'

import { getGroupArea } from '../DisplayProvider/utils'
import { safeParseArea } from '../formControls/Area/utils'
import { isAbsolute, isFlex, isGrid } from '../guards'

export const onUnwrap = () => {
  const dispatch = store.dispatch
  const state = store.getState().project
  const block = getBlock(state)
  const device = getDeviceMode(state)
  const isDesktop = device === DeviceMode.desktop
  const selectedNodes = getSelectedNodeIds(state)
  const children = selectedNodes.map((id) => block?.schema.nodes[id]).filter(isNodeV2)
  const node = children.length === 1 ? block?.schema.nodes[selectedNodes[0]] : null
  const element = isNodeV2(node) && node.elementId ? block?.elements[node.elementId] : null
  const nodeStyle = isNodeV2(node) ? mergeNodeStyle(node.style, device) : null
  const parent = getParentNode(selectedNodes?.[0], block)
  const parentElement =
    parent && isNodeV2(parent) && parent.elementId ? block?.elements[parent.elementId] : null
  const grandParent = getParentNode(parent?.id || '', block)
  const grandElement =
    grandParent && isNodeV2(grandParent) && grandParent.elementId
      ? block?.elements[grandParent.elementId]
      : null

  if (block) {
    const isGroup =
      Boolean(isComplexElement(element)) ||
      ((isFlex(nodeStyle) || isAbsolute(nodeStyle) || isGrid(nodeStyle)) &&
        !(isSlotElement(parentElement) && parentElement.value.item?.value)) ||
      Boolean(isSlotElement(element) && element.value.item?.value)
    const isSlot = isSlotElement(grandElement) && grandElement.value.item?.value

    const toUngroupChildren =
      isFlex(nodeStyle) || isAbsolute(nodeStyle) || isGrid(nodeStyle)
        ? children[0].children.map((id) => block.schema.nodes[id]).filter(isNodeV2)
        : isComplexElement(element)
          ? element?.value.items.map(({ value }) => block.schema.nodes[value]).filter(isNodeV2)
          : isSlotElement(element) && element.value.item?.value
            ? [block.schema.nodes[element.value.item.value]].filter(isNodeV2)
            : children.map((n) => n)

    const oldParent = isGroup ? children[0] : parent
    const newParent = isSlot
      ? getParentNode(grandParent?.id, block)
      : isGroup
        ? parent
        : grandParent

    const area = isSlot
      ? safeParseArea(mergeNodeStyle(grandParent?.style || {}, device).gridArea)
      : oldParent
        ? safeParseArea(mergeNodeStyle(oldParent?.style || {}, device).gridArea)
        : getGroupArea(toUngroupChildren, device)

    if (newParent && oldParent) {
      if (!isDesktop) {
        return notify({
          type: NotificationType.info,
          message: t('editorV2.notifyStructureChangesRestricted'),
        })
      }
      const width = area.colEnd - area.colStart
      const height = area.rowEnd - area.rowStart
      const parentStyle = mergeNodeStyle(oldParent?.style || {}, device)
      const direction = parentStyle?.flexDirection.split('-')[0] || 'row'
      const index = newParent.children.indexOf(oldParent.id) + 1
      const clampRowStart = R.clamp(area.rowStart, area.rowEnd)
      const clampColStart = R.clamp(area.colStart, area.colEnd)
      const clampRowEnd = R.clamp(area.rowStart + 1, area.rowEnd)
      const clampColEnd = R.clamp(area.colStart + 1, area.colEnd)
      const cbs = toUngroupChildren.map((n, i) => {
        const rowStart =
          direction === 'row'
            ? area.rowStart
            : clampRowStart(Math.floor((height / toUngroupChildren.length) * i + area.rowStart))
        const rowEnd =
          direction === 'row'
            ? area.rowEnd
            : clampRowEnd(Math.floor((height / toUngroupChildren.length) * (i + 1) + area.rowStart))
        const colStart =
          direction === 'column'
            ? area.colStart
            : clampColStart(Math.floor((width / toUngroupChildren.length) * i + area.colStart))
        const colEnd =
          direction === 'column'
            ? area.colEnd
            : clampColEnd(Math.floor((width / toUngroupChildren.length) * (i + 1) + area.colStart))

        const newArea = { rowStart, rowEnd, colStart, colEnd }

        return R.compose(
          setGridArea(n.id, device, newArea),
          moveNode(oldParent.id, newParent.id, n.id, index),
        )
      }) as [(block: Block) => Block]

      dispatch(setNavbar({ tab: NavigationEnum.block }))
      dispatch(
        updateBlock({
          id: block.uuid,
          value: R.compose(
            R.ifElse(
              (block) => {
                const element = oldParent.elementId && block.elements[oldParent.elementId]
                if (element && isComplexElement(element)) {
                  return R.view(elementValueItemsLens(oldParent.elementId), block).length == 0
                } else if (element && isSlotElement(element)) {
                  return true
                } else {
                  return R.view(childrenLens(oldParent.id), block)?.length == 0
                }
              },
              R.compose(omitNode(oldParent.id), rejectNode(oldParent.id)),
              R.identity,
            ),
            ...cbs,
          )(block),
        }),
      )
      dispatch(setProjectNavigation({ nodeIds: toUngroupChildren.map((n) => n.id) }))
    }
  }
}
